<script setup>
import { mdiClose } from "@mdi/js";
import Icon from "./Icon.vue";

defineProps({
  /** @type {boolean} */
  modelValue: {
    type: Boolean,
    required: true,
  },
  /** @type {'small' | 'large' | 'auto' } */
  size: {
    type: String,
    required: false,
    default: "large",
  },
  showCloseButton: {
    type: Boolean,
    required: false,
    default: true,
  },
  closeOnBackdrop: {
    type: Boolean,
    required: false,
    default: true,
  },
});

const emit = defineEmits(["update:modelValue"]);

const close = () => emit("update:modelValue", false);
</script>

<template>
  <Teleport to="body">
    <div
      class="relative z-30"
      :class="{
        hidden: !modelValue,
        visible: modelValue,
      }"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

      <div class="fixed inset-0 z-30 w-screen overflow-y-auto px-2">
        <div
          class="flex min-h-full items-center justify-center sm:p-4 text-center"
          @mousedown.self.stop="closeOnBackdrop ? close() : null"
        >
          <div
            class="rounded-md relative transform overflow-auto flex flex-col justify-between sm:rounded-lg bg-white text-left shadow-xl transition-all w-full sm:my-8 sm:w-full"
            :class="{
              'sm:h-[80vh] sm:max-w-[80vw]': size === 'large',
              'sm:h-[60vh] sm:max-w-[60vw]': size === 'medium',
              'sm:h-auto max-w-[90vw] sm:max-w-[32rem]': size === 'small',
            }"
          >
            <div
              v-if="showCloseButton"
              class="absolute flex items-center justify-center top-4 right-4 cursor-pointer z-[1]"
              @click="close"
            >
              <Icon
                :path="mdiClose"
                :classes="'text-gray-500 w-6 h-6'"
              />
            </div>

            <div class="bg-white relative px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
              <slot />
            </div>

            <div
              v-if="$slots.footer"
              class="sticky bottom-0 z-50 bg-white px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6"
            >
              <slot name="footer" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
